import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Atsi from '@linc-technologies/linced-common/enums/au-atsi';
import NzMoeCitizenship from '@linc-technologies/linced-common/enums/nz-moe-citizenship';
import NzMoeEthnicity from '@linc-technologies/linced-common/enums/nz-moe-ethnicity';
import NzMoeIwi from '@linc-technologies/linced-common/enums/nz-moe-iwi';
import NzMoeSchoolHistoric from '@linc-technologies/linced-common/enums/nz-moe-school-historic';
import NzMoeSubject from '@linc-technologies/linced-common/enums/nz-moe-subject';
import NzMoeSubjectYearLevel from '@linc-technologies/linced-common/enums/nz-moe-subject-year-level';
import NzMoeStp from '@linc-technologies/linced-common/enums/nz-moe-stp';
import AuCoreCountryBirth from '@linc-technologies/linced-common/enums/aucore-country-birth';
import AuCoreHomeLanguage from '@linc-technologies/linced-common/enums/aucore-home-language';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import type BaseEnum from '@linc-technologies/linced-common/enums/base-enum';
import type { EnumEntry } from '@linc-technologies/linced-common/enums/base-enum';
import type Permission from '@linc-technologies/linced-common/models/events/permission';
import type Field from '@linc-technologies/linced-common/models/people-schema/field';
import type FieldOptionModel from '@linc-technologies/linced-common/models/people-schema/field-option';

// Fields whose options come from an enum, by field key.
const fields: Readonly<Record<string, BaseEnum | undefined>> = {
  'nzMOE:countryCitizenship': NzMoeCitizenship,
  'core:countryBirth': NzMoeCitizenship,
  'nzMOE:ethnicity': NzMoeEthnicity,
  'nzMOE:iwi': NzMoeIwi,
  'nzMOE:previousSchool': NzMoeSchoolHistoric,
  'nzMOE:subject1': NzMoeSubject,
  'nzMOE:subject2': NzMoeSubject,
  'nzMOE:subject3': NzMoeSubject,
  'nzMOE:subject4': NzMoeSubject,
  'nzMOE:subject5': NzMoeSubject,
  'nzMOE:subject6': NzMoeSubject,
  'nzMOE:subject7': NzMoeSubject,
  'nzMOE:subject8': NzMoeSubject,
  'nzMOE:subject9': NzMoeSubject,
  'nzMOE:subject10': NzMoeSubject,
  'nzMOE:subject11': NzMoeSubject,
  'nzMOE:subject12': NzMoeSubject,
  'nzMOE:subject13': NzMoeSubject,
  'nzMOE:subject14': NzMoeSubject,
  'nzMOE:subject15': NzMoeSubject,
  'nzMOE:subject1YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject2YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject3YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject4YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject5YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject6YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject7YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject8YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject9YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject10YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject11YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject12YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject13YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject14YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:subject15YearLevel': NzMoeSubjectYearLevel,
  'nzMOE:stp': NzMoeStp,
  'auCore:countryBirth': AuCoreCountryBirth,
  'auCore:homeLanguage': AuCoreHomeLanguage,
  'core:atsi': Atsi,
};

export function getFieldOptions(key: string): readonly EnumEntry[] {
  // Own keys only, so inherited names like `toString` aren't enums.
  const options = Object.hasOwn(fields, key) ? fields[key] : undefined;
  return options ? options.values : [];
}

// The attributes of a people-schema/field-option. Plain objects with these
// keys also match.
export type FieldOption = Pick<
  FieldOptionModel,
  'value' | 'label' | 'valueInt'
>;

// A field's options come from its schema, or from a frontend enum when
// `optionsFromEnum` is set.
export type FieldOptionSource = FieldOption | EnumEntry;

// The parts of a people-schema/field that flattenList reads.
// permission-reply sends an events/permission. It has no optionsFromEnum.
export type FlattenField = Pick<
  Field | Permission,
  'key' | 'dataType' | 'options'
> &
  Partial<Pick<Field, 'optionsFromEnum'>>;

// A plain array, or the valueList of a dynamic-field/value.
interface FlattenValues extends Iterable<unknown> {
  map<U>(callback: (value: unknown) => U): U[];
}

export function flattenList(
  values: FlattenValues,
  field: FlattenField | null | undefined,
): string | undefined {
  if (!field || isEmpty(field)) {
    return;
  }

  // Get options from field or enum as appropriate
  let options: Iterable<FieldOptionSource> = field.options;
  if (field.optionsFromEnum) {
    options = getFieldOptions(field.key);
  }

  if (!isEmpty(field) && options && !isEmpty(options)) {
    let option: FieldOptionSource | undefined;
    values = values.map((value) => {
      if (field.dataType === DataType.INTEGER) {
        option = findBy(options, 'valueInt', value);
      } else {
        option = findBy(options, 'value', value);
      }
      if (!option || isEmpty(option.label)) {
        return value;
      }
      return option.label;
    });
  }

  return [...values].join(', ');
}
